import { LooseAutocomplete } from 'UIComponents/types/type-utils';
import {
  defineMessage,
  AudienceFilter,
  defineTopic,
  TopicMessages,
} from 'magpie-lib/models';
import * as t from 'ts-schema';

const insertVideoMessage = defineMessage({
  type: '@@video-components-external/utility/insert-video' as const,
  payloadSchema: t.object({
    video: t.object({
      sourceFileId: t.number,
    }),
  }),
});

const bulkInsertVideosMessage = defineMessage({
  type: '@@video-components-external/utility/bulk-insert-videos' as const,
  payloadSchema: t.object({
    videos: t.array(
      t.object({
        sourceFileId: t.number,
      })
    ),
  }),
});

const videoDeletedMessage = defineMessage({
  type: '@@video-components-external/video-management/video-deleted' as const,
  payloadSchema: t.object({
    sourceFileId: t.number,
    videoTitle: t.string,
  }),
});

const clipPublishedMessage = defineMessage({
  type: '@@video-components-external/video-management/clip-published' as const,
  payloadSchema: t.object({
    renderId: t.number,
  }),
});

// Deprecated: Use clipPublishedMessage instead
// Kept for backwards compatibility with content-media-composition
const variantPublishedMessage = clipPublishedMessage;

export interface PlainTextAlert {
  type: LooseAutocomplete<'info' | 'warning' | 'danger' | 'success' | 'tip'>;
  message?: string;
  titleText?: string;
}

const notifyUserMessage = defineMessage({
  type: '@@video-components-external/video-management/notify-user' as const,
  payloadSchema: t.custom(
    'UserNotificationPayload',
    (value: unknown): value is PlainTextAlert => {
      return typeof value === 'object' && value !== null && 'type' in value;
    }
  ),
});

const videoPanelClosedMessage = defineMessage({
  type: '@@video-components-external/video-management/video-panel-closed' as const,
  payloadSchema: t.object({}),
});

export const VideoManagementMessages = {
  insertVideoMessage,
  bulkInsertVideosMessage,
  clipPublishedMessage,
  variantPublishedMessage, // Deprecated: Use clipPublishedMessage
  notifyUserMessage,
  videoDeletedMessage,
  videoPanelClosedMessage,
};

export const videoManagementTopicDef = defineTopic({
  name: '@@video-components-external/video-management' as const,
  messages: [
    VideoManagementMessages.insertVideoMessage,
    VideoManagementMessages.bulkInsertVideosMessage,
    VideoManagementMessages.clipPublishedMessage,
    VideoManagementMessages.notifyUserMessage,
    VideoManagementMessages.videoDeletedMessage,
    VideoManagementMessages.videoPanelClosedMessage,
  ],
  enableRuntimeValidation: false,
  audience: AudienceFilter.builder().build(),
});

export type VideoManagementMessage = TopicMessages<
  typeof videoManagementTopicDef
>;
